import { useCallback } from 'react';
import { Box, Menu } from '@mui/material';
import { usePopupState, bindTrigger, bindMenu, anchorRef } from 'material-ui-popup-state/hooks';

import { Membership_State, Membership_Type } from '@occc/graphql';
import { Typography, ArrowDropDownIcon } from '@occc/ui-design-system';

import { MemberTypeMenuItem } from './MemberTypeMenuItem';
import { memberEmployeeStatusLabels, memberEmployeeStatusOptions } from './constants';
import { MembershipTypeIcon } from './MembershipTypeIcon';

export type MemberEmployeeStatusProps = {
  selectedType: Membership_Type;
  onMemberEmployeeTypeChange: (type: Membership_Type) => void;
  isUserPermittedToUpdateMemberType: boolean;
  selectedState: Membership_State;
};

export const MemberTypeDropdown = ({
  selectedType,
  isUserPermittedToUpdateMemberType,
  onMemberEmployeeTypeChange,
  selectedState,
}: MemberEmployeeStatusProps) => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'state-menu' });
  const isEditable = selectedState !== Membership_State.Deleted;

  const onSelect = useCallback(
    (value: Membership_Type) => {
      onMemberEmployeeTypeChange(value);
      popupState.close();
    },
    [onMemberEmployeeTypeChange, popupState],
  );

  if (!isUserPermittedToUpdateMemberType) {
    return (
      <Box display='flex' alignItems='center'>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'common.white',
            color: 'text.primary',
            borderRadius: 24,
            px: 0.75,
            height: 26,
          }}
        >
          <MembershipTypeIcon isSelected membershipType={selectedType} color='primary' />
          <Typography component='span' variant='body2'>
            {memberEmployeeStatusLabels[selectedType]}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box display='flex' alignItems='center' data-testid='member-type-menu-wrapper'>
      <Box
        role='button'
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'common.white',
          color: 'text.primary',
          borderRadius: 24,
          px: 0.75,
          cursor: isEditable ? 'pointer' : 'not-allowed',
          '&:hover': { opacity: isEditable ? 0.8 : 1 },
        }}
        ref={anchorRef(popupState)}
        {...bindTrigger(popupState)}
      >
        <MembershipTypeIcon isSelected membershipType={selectedType} color='primary' />
        <Typography component='span' variant='body2'>
          {memberEmployeeStatusLabels[selectedType]}
        </Typography>
        <ArrowDropDownIcon />
      </Box>
      {isEditable && (
        <Menu {...bindMenu(popupState)}>
          {memberEmployeeStatusOptions.map((value) => {
            return (
              <MemberTypeMenuItem
                key={value}
                onSelect={onSelect}
                value={value}
                selected={selectedType === value}
              />
            );
          })}
        </Menu>
      )}
    </Box>
  );
};
